/**
 * Turning a Word document into report sections.
 *
 * The point of this import is **structure**, not text. Anyone can copy and
 * paste a document's words; what is worth automating is recovering its
 * headings, its tables and its hierarchy, so the result arrives as a report
 * that can be reordered, commented on and exported rather than as one enormous
 * paragraph.
 *
 * So the conversion runs in two stages, and they are separate on purpose:
 *
 *   1. `mammoth` turns the `.docx` into HTML. That is the part that
 *      understands the file format, and it lives at the edge.
 *   2. `sectionsFromHtml` turns that HTML into pieces. That is pure, takes a
 *      string, and can be tested without a file.
 *
 * A **piece** is one section the import would add, together with the
 * paragraphs it is made of. The paragraphs are kept because a real document
 * carries a good deal nobody wants in a report: a covering note, a page of
 * instructions to the preparer, a paragraph of internal comment. Somebody
 * importing has to be able to leave those behind, and leaving them behind has
 * to be possible one paragraph at a time, not only one chapter at a time.
 *
 * The paragraphs are not sections themselves. A forty-paragraph chapter split
 * into forty sections is an outline nobody can use, so a run of prose stays
 * one text section and the choosing happens inside it.
 *
 * A table's formatting takes a third route, because HTML has no words for
 * a cell's shading or its ink: `docx.ts` reads it off the document's XML,
 * and `sectionsFromHtml` lays it over the tables it builds, one look per
 * table in document order. Optional, and stripped again by `withoutLooks`
 * when the person importing would rather have plain cells.
 *
 * Every scrap of markup is sanitised on the way through. A `.docx` is an
 * attachment somebody was sent, and it is exactly the kind of thing an
 * attacker uses.
 */

import { looksNumeric } from '@/domain/figures'
import { sanitiseHtml, textOf } from '@/domain/sanitise'
import {
  createSection,
  emptyCell,
  newId,
  type HeadingSection,
  type Section,
  type TableCell,
  type TableSection,
  type TextSection,
} from '@/domain/section'
import { readDocumentLooks } from '@/features/import/docx'
import type { CellLook, LookGrid } from '@/features/import/styles'
import { MOST_SPAN } from '@/sections/table/spans'

/**
 * One block of prose, as it sits in the document.
 *
 * A paragraph, a list, a quotation: whatever Word had at the top level. Held
 * separately so it can be shown, and dropped, on its own.
 */
export interface ImportPart {
  readonly id: string
  /** Sanitised, so what the preview shows is exactly what would be added. */
  readonly html: string
  /** The words, for the outline row and for judging whether it is worth keeping. */
  readonly text: string
}

/** One section the import would add. */
export interface ImportPiece {
  readonly id: string
  readonly section: Section
  /**
   * How far under a heading this sits, for the review list.
   *
   * Derived from the document's own heading levels rather than from anything
   * the report does, because at this point there is no report yet: it is the
   * shape of the file being shown back to the person who chose it.
   */
  readonly depth: number
  /** The paragraphs of a text section. Empty for everything else. */
  readonly parts: readonly ImportPart[]
}

/**
 * Something the import could not bring across.
 *
 * A value rather than a sentence, because the sentence has to be in the
 * language the person is working in and this file has no business knowing
 * which that is. Reported rather than silently swallowed: somebody who
 * imported a document with twelve images needs to know none of them came
 * across, before they send the report out.
 */
export type ImportNote =
  | { readonly kind: 'imagesLeftOut'; readonly count: number }
  | { readonly kind: 'simplified'; readonly count: number }

/** What an import produced, and what it could not. */
export interface ImportResult {
  readonly pieces: readonly ImportPiece[]
  readonly notes: readonly ImportNote[]
}

/** How deep a Word heading maps. Anything below is prose. */
function headingLevel(tag: string): 1 | 2 | 3 | 4 | null {
  switch (tag) {
    case 'h1':
      return 1
    case 'h2':
      return 2
    case 'h3':
      return 3
    case 'h4':
      return 4
    default:
      return null
  }
}

export { looksNumeric }

function cellFrom(cell: Element, look?: CellLook): TableCell {
  const value = (cell.textContent ?? '').trim().replace(/\s+/g, ' ')
  const made: TableCell = { value }
  if (looksNumeric(value)) made.numeric = true
  // Word marks totals by bolding them, which is the one piece of formatting
  // the HTML keeps: it is what tells a subtotal from a line item.
  if (cell.querySelector('strong, b') !== null || look?.strong) made.strong = true
  if (look?.align) made.align = look.align
  if (look?.fill) made.fill = look.fill
  if (look?.ink) made.ink = look.ink
  return made
}

/** How far a cell of the HTML says it runs, as a whole number of at least 1. */
function spanOf(cell: Element, name: 'colspan' | 'rowspan'): number {
  const value = Number.parseInt(cell.getAttribute(name) ?? '', 10)
  return Number.isFinite(value) && value > 1 ? Math.min(value, MOST_SPAN) : 1
}

/**
 * A Word table's rows as a rectangle, merged cells kept.
 *
 * The HTML gives a merged cell once, with how far it runs, and leaves out the
 * cells it runs over. A report table keeps every position, so those are filled
 * here with empty cells, laid out the way a browser lays out the same HTML:
 * each cell takes the first position in its row that a merge from above has
 * not already taken.
 *
 * The looks are read per cell the document draws, which is per element here,
 * so they are found by the element's place in its row rather than by column.
 */
function gridFrom(rows: readonly Element[], looks?: LookGrid): TableCell[][] {
  const lines: TableCell[][] = rows.map(() => [])
  const put = (line: number, column: number, cell: TableCell): void => {
    const cells = lines[line]
    if (cells) cells[column] = cell
  }

  rows.forEach((row, line) => {
    let column = 0
    Array.from(row.children).forEach((element, index) => {
      while (lines[line]?.[column] !== undefined) column += 1
      const colspan = spanOf(element, 'colspan')
      const rowspan = Math.min(spanOf(element, 'rowspan'), rows.length - line)

      const cell = cellFrom(element, looks?.[line]?.[index])
      if (colspan > 1) cell.colspan = colspan
      if (rowspan > 1) cell.rowspan = rowspan

      for (let down = 0; down < rowspan; down += 1) {
        for (let across = 0; across < colspan; across += 1) {
          put(line + down, column + across, down === 0 && across === 0 ? cell : emptyCell())
        }
      }
      column += colspan
    })
  })

  // Array holes where nothing was placed, read back as empty cells, so every
  // row comes out as wide as the widest.
  const width = Math.max(1, ...lines.map((cells) => cells.length))
  return lines.map((cells) => Array.from({ length: width }, (_, at) => cells[at] ?? emptyCell()))
}

function tableFrom(element: Element, title: string, looks?: LookGrid): TableSection | null {
  const rows = [...element.querySelectorAll('tr')]
  if (rows.length === 0) return null

  const [first] = rows
  if (!first) return null

  const base = createSection('table', title) as TableSection

  // A first row of headers, if Word said so; otherwise the first row is data
  // and the table gets an empty header, which the renderer then omits.
  const headerRow = first.querySelector('th') !== null ? first : null

  // Laid out whole, header included, because a header cell can run down into
  // the body; the looks are by row of the table as Word had it, the same way.
  const lines = gridFrom(rows, looks)
  return {
    ...base,
    header: headerRow ? (lines[0] ?? []) : [],
    rows: headerRow ? lines.slice(1) : lines,
  }
}

/**
 * The same pieces, with the tables' formatting taken off.
 *
 * Ids are kept, so a selection made against the formatted pieces still
 * applies. Bold stays, because it came from the words as much as from the
 * workbook: a bold total is a total whether or not it is also coloured.
 */
export function withoutLooks(pieces: readonly ImportPiece[]): readonly ImportPiece[] {
  return pieces.map((piece) => {
    if (piece.section.kind !== 'table') return piece
    // A merge is the table's shape rather than its formatting, so it stays.
    const plain = (cell: TableCell): TableCell => {
      const kept: TableCell = { value: cell.value }
      if (cell.numeric) kept.numeric = true
      if (cell.strong) kept.strong = true
      if (cell.colspan) kept.colspan = cell.colspan
      if (cell.rowspan) kept.rowspan = cell.rowspan
      return kept
    }
    return {
      ...piece,
      section: {
        ...piece.section,
        header: piece.section.header.map(plain),
        rows: piece.section.rows.map((row) => row.map(plain)),
      },
    }
  })
}

/** Whether any table among the pieces carries formatting worth a choice. */
export function hasLooks(pieces: readonly ImportPiece[]): boolean {
  return pieces.some(
    (piece) =>
      piece.section.kind === 'table' &&
      [...piece.section.header, ...piece.section.rows.flat()].some(
        (cell) => cell.align !== undefined || cell.fill !== undefined || cell.ink !== undefined,
      ),
  )
}

/**
 * A short title for a block of prose, so the outline row is not blank.
 *
 * Exported because the title has to be worked out again whenever paragraphs
 * are dropped: a section named after a paragraph that is no longer in it is
 * worse than one named after nothing.
 */
export function firstWords(text: string, fallback = 'Text'): string {
  const trimmed = text.trim().replace(/\s+/g, ' ')
  if (trimmed === '') return fallback
  if (trimmed.length <= 46) return trimmed
  return `${trimmed.slice(0, 46).replace(/\s+\S*$/, '')}...`
}

/** The prose of a text section, assembled from the parts that are kept. */
export function proseFrom(parts: readonly ImportPart[]): { html: string; title: string } {
  const html = parts.map((part) => part.html).join('')
  const title = firstWords(parts.map((part) => part.text).join(' '))
  return { html, title }
}

/**
 * Pieces from a document's HTML.
 *
 * Headings become their own sections, so the outline gets the document's
 * structure. Everything between two headings is gathered into one text piece,
 * whose paragraphs stay listed so they can be chosen between.
 *
 * The looks, when given, are one grid per table in document order, which is
 * the order the tables are met in here.
 */
export function sectionsFromHtml(html: string, looks: readonly LookGrid[] = []): ImportResult {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const pieces: ImportPiece[] = []
  const notes: ImportNote[] = []

  let parts: ImportPart[] = []
  let depth = 0
  let tables = 0
  let images = 0

  const flushProse = (): void => {
    const gathered = parts
    parts = []
    if (gathered.length === 0) return

    const { html: prose, title } = proseFrom(gathered)
    const base = createSection('text') as TextSection
    pieces.push({
      id: base.id,
      section: { ...base, title, html: prose },
      depth,
      parts: gathered,
    })
  }

  for (const node of [...parsed.body.children]) {
    const tag = node.tagName.toLowerCase()

    const level = headingLevel(tag)
    if (level !== null) {
      flushProse()
      const title = (node.textContent ?? '').trim()
      if (title) {
        const base = createSection('heading', title) as HeadingSection
        pieces.push({
          id: base.id,
          section: { ...base, level },
          depth: level - 1,
          parts: [],
        })
      }
      // Whatever follows belongs under this heading, whether or not the
      // heading itself had any text worth keeping.
      depth = level
      continue
    }

    if (tag === 'table') {
      flushProse()
      tables += 1
      const table = tableFrom(node, `Table ${tables}`, looks[tables - 1])
      if (table) pieces.push({ id: table.id, section: table, depth, parts: [] })
      continue
    }

    images += node.querySelectorAll('img').length

    const safe = sanitiseHtml(node.outerHTML)
    const text = textOf(safe)
    // Word documents are full of empty paragraphs used as spacing. They carry
    // nothing, and listing them would bury the paragraphs that do.
    if (text.trim() !== '') parts.push({ id: newId(), html: safe, text })
  }

  flushProse()

  // A document that yielded nothing needs no note: the empty result says it,
  // and the dialog has a better place to say it than a list of losses.
  if (images > 0) notes.push({ kind: 'imagesLeftOut', count: images })

  return { pieces, notes }
}

/**
 * Read a `.docx`.
 *
 * `mammoth` is loaded on demand: it is a large dependency, and most sessions
 * never import anything. Paying for it on first use rather than on every page
 * load is the difference between a builder that opens instantly and one that
 * does not.
 */
export async function importWord(file: File): Promise<ImportResult> {
  const mammoth = await import('mammoth')
  const buffer = await file.arrayBuffer()

  const converted = await mammoth.convertToHtml(
    { arrayBuffer: buffer },
    {
      // Word's own outline levels, mapped to real headings. Without this,
      // mammoth reports styled paragraphs and the structure is lost.
      styleMap: [
        "p[style-name='Heading 1'] => h1:fresh",
        "p[style-name='Heading 2'] => h2:fresh",
        "p[style-name='Heading 3'] => h3:fresh",
        "p[style-name='Heading 4'] => h4:fresh",
        "p[style-name='Titre 1'] => h1:fresh",
        "p[style-name='Titre 2'] => h2:fresh",
        "p[style-name='Titre 3'] => h3:fresh",
      ],
    },
  )

  const result = sectionsFromHtml(converted.value, await documentLooks(buffer))

  // What mammoth itself could not handle, said once rather than per instance.
  const kinds = new Set(converted.messages.map((message) => message.message))
  const extra: readonly ImportNote[] =
    kinds.size > 0 ? [{ kind: 'simplified', count: kinds.size }] : []

  return { pieces: result.pieces, notes: [...result.notes, ...extra] }
}

/**
 * The tables' formatting, read off the document's own XML.
 *
 * The document is a zip, and the spreadsheet library's container reader
 * opens one without knowing or caring what is inside. Loaded on demand for
 * the same reason mammoth is, and nothing here is allowed to fail the
 * import: a document whose formatting cannot be read is still a document.
 */
async function documentLooks(buffer: ArrayBuffer): Promise<readonly LookGrid[]> {
  try {
    const { CFB } = await import('xlsx')
    const container = CFB.read(new Uint8Array(buffer), { type: 'array' })
    const decoder = new TextDecoder()
    const text = (path: string): string | null => {
      const entry = CFB.find(container, path)
      return entry?.content ? decoder.decode(entry.content as Uint8Array) : null
    }
    return readDocumentLooks(text('/word/document.xml'), text('/word/styles.xml'))
  } catch {
    return []
  }
}
